// File: questionService.ts
import {
  CreateQuestionInput,
  Question,
  ReorderQuestionsInput,
  UpdateQuestionInput
} from '@/types/questionTypes';
import {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';

// Define a more precise type for the getAccessTokenSilently function
type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;

// Update the type for getAccessTokenWithPopup
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Helper function to construct the base URL for questions of a specific template
const getQuestionsEndpoint = (templateId: number) =>
  `${API_BASE_URL}/templates/${templateId}/questions`;

/**
 * Helper function to acquire Auth0 access token.
 * Tries silently, then falls back to popup.
 * (Copied from templateService.ts - ideally, this would be in a shared utility)
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      // scope: "read:questions write:questions" // Add specific scopes if needed
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn('Silent token acquisition failed for QuestionService:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for QuestionService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed to return a token for QuestionService.'
        );
      }
      return token;
    }
    throw error; // Rethrow other errors
  }
};

/**
 * Creates a new question for a template.
 * @param templateId - The ID of the template this question belongs to.
 * @param input - The data for the new question.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The created question.
 */
export const createQuestion = async (
  templateId: number,
  input: CreateQuestionInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Question> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const endpoint = getQuestionsEndpoint(templateId); // e.g., /api/templates/123/questions

  const response = await fetch(endpoint, {
    // POST to the base questions URL for the template
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error creating question:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to create question. Status: ${response.status}`
    );
  }
  return (await response.json()) as Question;
};

/**
 * Retrieves all questions for a specific template.
 * @param templateId - The ID of the template.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns A list of questions.
 */
export const getAllQuestionsForTemplate = async (
  templateId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Question[]> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const endpoint = getQuestionsEndpoint(templateId);

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(
      `Error fetching questions for template ${templateId}:`,
      errorData
    );
    throw new Error(
      errorData.message ||
        `Failed to fetch questions. Status: ${response.status}`
    );
  }
  return (await response.json()) as Question[];
};

/**
 * Retrieves a specific question by its ID within a template.
 * @param templateId - The ID of the template.
 * @param questionId - The ID of the question to retrieve.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The requested question or null if not found.
 */
export const getQuestionById = async (
  templateId: number,
  questionId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Question | null> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const endpoint = `${getQuestionsEndpoint(templateId)}/${questionId}`;

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (response.status === 404) {
    console.warn(
      `Question with ID '${questionId}' in template '${templateId}' not found.`
    );
    return null;
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(`Error fetching question ID ${questionId}:`, errorData);
    throw new Error(
      errorData.message ||
        `Failed to fetch question. Status: ${response.status}`
    );
  }
  return (await response.json()) as Question;
};

/**
 * Updates an existing question.
 * @param templateId - The ID of the template.
 * @param questionId - The ID of the question to update.
 * @param input - The data to update the question with. It must include the question's ID.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The updated question, or null if not found.
 */
export const updateQuestion = async (
  templateId: number,
  questionId: number,
  input: UpdateQuestionInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Question | null> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  if (input.questionId !== questionId) {
    console.warn(
      `Mismatch between questionId in path (${questionId}) and input DTO (${input.questionId}). Ensuring path ID is used for consistency.`
    );
  }
  // Ensure the payload 'questionId' matches the 'questionId' from the route.
  const payload: UpdateQuestionInput = { ...input, questionId: questionId };
  const endpoint = `${getQuestionsEndpoint(templateId)}/${questionId}`;

  const response = await fetch(endpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(payload)
  });

  if (response.status === 404) {
    console.warn(
      `Question with ID '${questionId}' in template '${templateId}' not found for update.`
    );
    return null;
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(`Error updating question ID ${questionId}:`, errorData);
    throw new Error(
      errorData.message ||
        `Failed to update question. Status: ${response.status}`
    );
  }
  return (await response.json()) as Question;
};

/**
 * Deletes a question by its ID.
 * @param templateId - The ID of the template.
 * @param questionId - The ID of the question to delete.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns True if deletion was successful (204 No Content), false if not found (404).
 */
export const deleteQuestion = async (
  templateId: number,
  questionId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const endpoint = `${getQuestionsEndpoint(templateId)}/${questionId}`;

  const response = await fetch(endpoint, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (response.status === 204) {
    return true; // Successfully deleted
  }
  if (response.status === 404) {
    console.warn(
      `Question with ID '${questionId}' in template '${templateId}' not found for deletion.`
    );
    return false; // Not found
  }
  if (!response.ok) {
    let errorDetail = response.statusText;
    try {
      const errorData = await response.json();
      errorDetail = errorData.message || errorDetail;
    } catch (e) {
      /* Ignore if parsing fails */
    }
    console.error(`Error deleting question ID ${questionId}:`, errorDetail);
    throw new Error(
      `Failed to delete question. Status: ${response.status}, Detail: ${errorDetail}`
    );
  }
  // Should not be reached if !response.ok throws or 204/404 are handled.
  return false;
};

/**
 * Reorders questions within a specific template.
 * @param templateId - The ID of the template where questions are being reordered.
 * @param input - The reordering instructions.
 * @param userId - The ID of the user performing the action.
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns True if reordering was successful, otherwise throws an error.
 */
export const reorderQuestions = async (
  templateId: number,
  input: ReorderQuestionsInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  // API returns 200 OK with a message, or other statuses.
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const endpoint = `${getQuestionsEndpoint(templateId)}/reorder`;

  const response = await fetch(endpoint, {
    method: 'PATCH', // As defined in the C# API endpoints
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(input)
  });

  if (response.ok) {
    // Expecting 200 OK based on API endpoint
    return true;
  } else {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(
      `Error reordering questions for template ${templateId}:`,
      errorData
    );
    throw new Error(
      errorData.message ||
        `Failed to reorder questions. Status: ${response.status}`
    );
  }
};
